'use client';

import { useState, useEffect, useRef } from 'react';
import { SnaplandModal, SnaplandModalContent, SnaplandModalHeader, SnaplandModalTitle, SnaplandModalClose, SnaplandModalBody } from './snaplandmodal';
import { Button } from './button';
import { Download, ExternalLink, X, ZoomIn, ZoomOut, RotateCw, FileText, XCircle } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { MessageLoader } from "@/components/ui/chat/MessageLoader";

interface PdfViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  title?: string;
}

export function PdfViewer({ isOpen, onClose, pdfUrl, title }: PdfViewerProps) {
  const { t } = useI18n();
  const displayTitle = title || t("chat.pdf_viewer");
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string>(pdfUrl);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = currentUrl || pdfUrl;
    link.download = displayTitle || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenInNewTab = () => {
    window.open(currentUrl || pdfUrl, '_blank');
  };

  // Reset loading state when PDF URL changes
  useEffect(() => {
    if (pdfUrl && isOpen) {
      setIsLoading(true);
      setHasError(false);
      
      // Replace http:// with https:// for external URLs
      const normalizedUrl = pdfUrl.startsWith('http://') 
        ? pdfUrl.replace('http://', 'https://')
        : pdfUrl;
      
      setCurrentUrl(normalizedUrl);
      
      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      // Set a timeout to detect if PDF fails to load (after 15 seconds)
      errorTimeoutRef.current = setTimeout(() => {
        setHasError(true);
        setIsLoading(false);
      }, 15000);
    } else if (!isOpen) {
      // Reset states when modal closes
      setIsLoading(true);
      setHasError(false);
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    }
    
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [pdfUrl, isOpen]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  const handleIframeError = () => {
    setHasError(true);
    setIsLoading(false);
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
  };

  const modalBaseId = 'pdf-viewer-modal';

  return (
    <SnaplandModal open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SnaplandModalContent
        // SnaplandModalContent defaults to overflow-visible (for dropdowns/popovers),
        // but for the PDF viewer we want a strict viewport: header + scrollable body.
        className="max-w-7xl w-full h-[90vh] p-0 flex flex-col overflow-hidden"
        showCloseButton={false}
      >
        <SnaplandModalHeader showCloseButton={false} className="p-4" snaplandId={`${modalBaseId}-header`}>
          <div className="flex items-center justify-between w-full">
            <SnaplandModalTitle className="text-lg max-w-2xl truncate flex items-center gap-2" title={displayTitle} snaplandId={`${modalBaseId}-title`}>
              <FileText className="h-5 w-5 flex-shrink-0" />
              <span className="truncate">{displayTitle}</span>
            </SnaplandModalTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="bg-white hover:bg-gray-100"
                snaplandId={`${modalBaseId}-zoom-out`}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-white/80 min-w-[60px] text-center" data-snapland-id={`${modalBaseId}-zoom-scale`}>
                {Math.round(scale * 100)}%
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={scale >= 3}
                className="bg-white hover:bg-gray-100"
                snaplandId={`${modalBaseId}-zoom-in`}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRotate}
                className="bg-white hover:bg-gray-100"
                snaplandId={`${modalBaseId}-rotate`}
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="bg-white hover:bg-gray-100"
                snaplandId={`${modalBaseId}-download`}
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInNewTab}
                className="bg-white hover:bg-gray-100"
                snaplandId={`${modalBaseId}-external-link`}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
              <SnaplandModalClose asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white hover:bg-gray-100"
                  snaplandId={`${modalBaseId}-close`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </SnaplandModalClose>
            </div>
          </div>
        </SnaplandModalHeader>
        
        <SnaplandModalBody className="p-0 flex-1 overflow-hidden flex">
          <div className="w-full h-full overflow-auto">
            <div
              className="relative w-full h-full"
              style={{
                transform: `scale(${scale}) rotate(${rotation}deg)`,
                transformOrigin: 'top center',
              }}
            >
              {currentUrl || pdfUrl ? (
                <>
                  {isLoading && !hasError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white rounded-md shadow-lg z-10">
                      <div className="text-center">
                        <div className="flex justify-center mb-4">
                          <MessageLoader />
                        </div>
                        <p className="text-gray-600">{t("chat.loading_pdf")}</p>
                      </div>
                    </div>
                  )}
                  {hasError ? (
                    <div className="flex items-center justify-center h-full w-full rounded-md">
                      <div className="text-center p-8">
                        <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                        <p className="text-gray-700 text-lg font-medium mb-2">{t("chat.unable_to_load_pdf")}</p>
                        <p className="text-gray-500 text-sm mb-6">{t("chat.pdf_load_error_message")}</p>
                        <Button
                          onClick={handleOpenInNewTab}
                          className="bg-[hsl(var(--sidebar-background))] text-white hover:bg-[hsl(var(--sidebar-accent))]"
                        >
                          <ExternalLink className="h-4 w-4 me-2" />
                          {t("chat.open_in_new_tab")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <iframe
                      ref={iframeRef}
                      src={currentUrl || pdfUrl}
                      className="w-full h-full border-0 shadow-lg bg-white"
                      title={displayTitle}
                      onLoad={handleIframeLoad}
                      onError={handleIframeError}
                      key={currentUrl || pdfUrl}
                    />
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full w-full bg-gray-100 rounded-md">
                  <div className="text-center">
                    <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">{t("chat.no_pdf_url")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SnaplandModalBody>
      </SnaplandModalContent>
    </SnaplandModal>
  );
}
